/** A plain table row at the default font size; used until a real row can be measured. */
const FALLBACK_ROW_HEIGHT = 21;

/** Height of one rendered data row inside `wrapper`, or the fallback until one exists. */
export function measuredRowHeight(wrapper: HTMLElement | null | undefined): number {
  // `.ant-table-row` = data rows only, not antd's measure row or the empty placeholder.
  const row = wrapper?.querySelector<HTMLElement>('.ant-table-row');
  return row && row.offsetHeight > 0 ? row.offsetHeight : FALLBACK_ROW_HEIGHT;
}

/**
 * Rows that must fit for filling to be worth doing — a GATE, not a forced minimum: under it we
 * decline entirely rather than squeeze the body. Filling exists to remove the container's
 * scrollbar, so when something below the table makes it scroll anyway, filling would only
 * shrink the table and take away the scrollbar the user needs to reach it.
 */
const MIN_VISIBLE_ROWS = 4;

/** One row is enough in a tile: its size is deliberate and gridstack gives it its own overflow. */
const MIN_VISIBLE_ROWS_CONTAINER = 1;

/**
 * Space that must be left over for filling to be worth doing. Keyed on the container actually
 * resolved, not on the caller, so a box table gets the tile rule in a dashboard and the page
 * rule on a page without knowing which it is in.
 */
function worthFillingHeight(wrapper: HTMLElement, isTile: boolean): number {
  // An empty table shows the "No data" placeholder, which is far taller than a row; squeezing
  // below it gains nothing and costs a scrollbar (which then provokes a spurious horizontal
  // one in Firefox). So ask for the placeholder's own height and decline below that.
  if (!wrapper.querySelector('.ant-table-row')) {
    const placeholder = wrapper.querySelector<HTMLElement>('.ant-table-placeholder');
    if (placeholder && placeholder.offsetHeight > 0) return placeholder.offsetHeight;
  }

  return measuredRowHeight(wrapper) * (isTile ? MIN_VISIBLE_ROWS_CONTAINER : MIN_VISIBLE_ROWS);
}

/**
 * Breathing room under a page-filling table — spaceBelow measures a true zero on most pages,
 * which would put the last row flush against the viewport edge. Not applied in a tile.
 *
 * Also the exact step by which the measurement drains itself against a content-height
 * container: every other term cancels there, this one does not. See the trap below.
 */
const PAGE_BOTTOM_GAP = 12;

/** Slack for "is this sibling below me or beside me" — sub-pixel rects, odd negative margins. */
const SIDE_BY_SIDE_EPSILON = 2;

/**
 * A dashboard cell, on either dashboard: its height comes from the LAYOUT, which is what a fill
 * measurement needs. Such a box is MARKED rather than detected, because "does this box have a
 * height of its own" cannot be read from computed style — see the trap below.
 *
 * `.grid-stack-item-content` is gridstack's, `.xm-dashboard-tile` is ours. Anything else that
 * gains a definite height and wants a table to fill it belongs here.
 */
const TILE_SELECTOR = '.grid-stack-item-content, .xm-dashboard-tile';

/**
 * The element whose height the body is fitted into, or null if there is nothing suitable.
 *
 * A tile wins over the nearest scrollable, which inside one is `.xm-table-with-toolbar` —
 * whose height is downstream of ours, so it would feed our own result back in.
 */
export interface FillTarget {
  readonly el: HTMLElement;
  /** A dashboard tile, as opposed to a page/modal/drawer scroll viewport. */
  readonly isTile: boolean;
}

export function resolveFillContainer(wrapper: HTMLElement): FillTarget | null {
  const tile = wrapper.closest<HTMLElement>(TILE_SELECTOR);
  if (tile) return { el: tile, isTile: true };
  const scroller = closestVerticalFillContainer(wrapper);
  return scroller ? { el: scroller, isTile: false } : null;
}

/**
 * Nearest ancestor whose height can actually BOUND ours. Not `DomUtil.closestScrollable`: that
 * stops at the first ancestor scrolling on either axis. Inline-level boxes are skipped (they
 * shrink to their content, so their height is downstream of ours) by CONTINUING up, not giving
 * up. A flex ITEM is blockified, so `.xm-table-with-toolbar` is correctly not skipped.
 *
 * THE TRAP, which this cannot detect: a box that only means to scroll horizontally. CSS
 * promotes `overflow-y: visible` to `auto` whenever `overflow-x` is `auto`, and no computed
 * style tells that apart from an authored one. Against such a content-height box the table
 * drains itself PAGE_BOTTOM_GAP per re-measure. The fix belongs in the CSS — a
 * horizontally-scrolling box states `overflow-y: hidden`; see alertCfg.less.
 */
function closestVerticalFillContainer(el: HTMLElement): HTMLElement | null {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const style = getComputedStyle(node);
    if (!/(auto|scroll)/.test(style.overflowY)) continue;
    if (style.display.startsWith('inline')) continue;
    return node;
  }
  return null;
}

/**
 * Height available to a table body inside its container, derived entirely from measurement.
 * Null when nothing can be measured yet (so the caller keeps its provisional value); no
 * `height` when the leftover is too small to be worth filling — see MIN_VISIBLE_ROWS.
 *
 * Measured in the container's CONTENT coordinates, not viewport ones: `bodyRect.top` moves as
 * things scroll, so a viewport-based measure grows the table when the page is scrolled down,
 * which lengthens the page, which scrolls further — no fixed point. Anchoring inside the
 * container's content box takes every `scrollTop` back out.
 *
 * ASSUMES the container's height does not depend on its content — see the trap above.
 */
export interface BodyHeightMeasurement {
  /**
   * Height to apply to the body, or undefined when filling would achieve nothing here —
   * something below the table already forces the container to scroll.
   */
  readonly height?: number;
  /**
   * The container's own viewport height. A sane cap for a virtual table that is NOT filling:
   * rc-virtual has to be given a number, and one screenful keeps it from rendering everything
   * without pretending to fit the table into leftovers.
   */
  readonly viewport: number;
  /** Cap for a table that does NOT fill: header + body fit the viewport. Undefined in a tile. */
  readonly screen?: number;
}

export function measureAvailableBodyHeight(wrapper: HTMLElement): BodyHeightMeasurement | null {
  const target = resolveFillContainer(wrapper);
  if (!target) return null;
  const { el: scroller, isTile } = target;

  const viewport = scroller.clientHeight;
  if (viewport <= 0) return null;

  // Measure from the body, not the wrapper: it is where the scrollable area starts and its own
  // height never moves its top, so this cannot feed back.
  //
  // A table that is NOT filling has no `.ant-table-body` — rc-table renders one
  // `.ant-table-content` holding the header too, whose top is therefore higher. Measuring that
  // without compensating made the space look bigger and flipped the fill decision every
  // re-measure, and since declining is what removes the body, the flip sustained itself
  // ("Maximum update depth exceeded"). Only the header has to be added back.
  const body = wrapper.querySelector<HTMLElement>('.ant-table-body, .ant-table-tbody-virtual-holder');
  const anchor = body ?? wrapper.querySelector<HTMLElement>('.ant-table-content') ?? wrapper;
  const headerHeight = wrapper.querySelector<HTMLElement>('.ant-table-thead')?.offsetHeight ?? 0;
  const headerCompensation = body ? 0 : headerHeight;

  const anchorRect = anchor.getBoundingClientRect();
  const scrollerRect = scroller.getBoundingClientRect();
  // clientHeight is measured inside the border, so the offset must be too (clientTop). Adding
  // back every scrollTop in between turns the measured rect into a LAYOUT position.
  const offsetInContent = anchorRect.top - scrollerRect.top - scroller.clientTop
    + scroller.scrollTop + scrolledAbove(anchor, scroller) + headerCompensation;

  const screen = viewport - headerHeight;
  const declined: BodyHeightMeasurement = { viewport, screen: !isTile && screen > 0 ? Math.floor(screen) : undefined };

  const below = spaceBelow(anchor, scroller);
  // Only the LAST filling table in a container fills; the ones above cap at `screen` (viewport
  // only). Two filling tables would measure each other with no fixed point — stacked tables on
  // Veeam Health Status kept hopping after F5.
  if (below.fillTableBelow) return declined;

  const gap = isTile ? 0 : PAGE_BOTTOM_GAP;
  const available = viewport - offsetInContent - below.height - gap;
  if (!Number.isFinite(available)) return null;

  // spaceBelow already totals the siblings after the table, so a page with graphs underneath
  // reports little room left and declines instead of shrinking to fit around them.
  if (available < worthFillingHeight(wrapper, isTile)) return declined;

  // Clamp for the mid-relayout transient where the anchor reads above the content top.
  return { height: Math.min(Math.floor(available), viewport), viewport };
}

/** Scroll offset of the containers between `anchor` and `container` — each has already moved
 *  `anchor`'s client rect up by its own `scrollTop`. */
function scrolledAbove(anchor: HTMLElement, container: HTMLElement): number {
  let total = 0;
  for (let el = anchor.parentElement; el && el !== container; el = el.parentElement) {
    total += el.scrollTop;
  }
  return total;
}

/** Set by XmTable on the wrapper of every `fillHeight` table. */
export const FILL_HEIGHT_CLASS = 'xm-fill-height';

/** Does `node` hold a rendered filling table? A hidden tab pane's (offsetHeight 0) does not count. */
function holdsFillTable(node: HTMLElement): boolean {
  if (node.classList.contains(FILL_HEIGHT_CLASS)) return node.offsetHeight > 0;
  return [...node.querySelectorAll<HTMLElement>(`.${FILL_HEIGHT_CLASS}`)].some(el => el.offsetHeight > 0);
}

interface SpaceBelow {
  readonly height: number;
  /** Another filling table sits below `from` in the same container. */
  readonly fillTableBelow: boolean;
}

/**
 * Everything that must still fit under `from` inside `scroller`: the bottom insets of every
 * wrapper in between plus every sibling rendered after it. This is what keeps a filling table
 * from pushing a save bar or footer off the bottom without any per-page slack constant.
 */
function spaceBelow(from: HTMLElement, scroller: HTMLElement): SpaceBelow {
  let total = 0;
  let fillTableBelow = false;
  for (let el: HTMLElement | null = from; el && el !== scroller; el = el.parentElement) {
    const style = getComputedStyle(el);
    total += parseFloat(style.marginBottom) || 0;
    const elBottom = el.getBoundingClientRect().bottom;

    for (let node = el.nextElementSibling; node; node = node.nextElementSibling) {
      if (!(node instanceof HTMLElement)) continue;
      const sibStyle = getComputedStyle(node);
      // Out-of-flow siblings take no vertical room (rc-virtual's h-scrollbar is one).
      if (sibStyle.display === 'none' || sibStyle.position === 'absolute' || sibStyle.position === 'fixed') continue;
      // "Following" in the DOM is not "below" on screen: the next `.ant-col` of an antd `Row`
      // sits BESIDE the table. Decided geometrically, which also covers grids, floats and a
      // `Row` that has wrapped.
      if (node.getBoundingClientRect().top < elBottom - SIDE_BY_SIDE_EPSILON) continue;
      total += node.offsetHeight
        + (parseFloat(sibStyle.marginTop) || 0)
        + (parseFloat(sibStyle.marginBottom) || 0);
      fillTableBelow ||= holdsFillTable(node);
    }

    const parent = el.parentElement;
    if (!parent) break;
    const parentStyle = getComputedStyle(parent);
    total += parseFloat(parentStyle.paddingBottom) || 0;
    // The scroller's own bottom border is already outside clientHeight — counting it here
    // would subtract it twice.
    if (parent !== scroller) total += parseFloat(parentStyle.borderBottomWidth) || 0;
  }
  return { height: total, fillTableBelow };
}

/**
 * Resolve a caller's `scroll.y` CSS length ('80vh', 'calc(100vh - 430px)') to pixels. Needed
 * only on the virtual path — rc-virtual computes its row window from a NUMBER and renders every
 * row when handed a string. Percentages return null: they would resolve against the probe's
 * host, not the caller's box.
 */
export function resolveCssHeightPx(value: string): number | null {
  if (typeof document === 'undefined') return null;
  if (value.includes('%')) return null;

  const probe = document.createElement('div');
  probe.style.cssText = 'position:absolute;top:0;left:0;visibility:hidden;pointer-events:none;width:0;';
  probe.style.height = value;
  // An invalid value leaves height unset, so the probe measures 0 and we fall through.
  if (!probe.style.height) return null;

  document.body.appendChild(probe);
  const px = probe.getBoundingClientRect().height;
  probe.remove();
  return px > 0 ? Math.floor(px) : null;
}
